import {
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react"
import { createPortal } from "react-dom"
import type { RunFacetsResponse, RunVerdictReasonTally } from "@nova-ai/shared"
import { Ban, Check, ListFilter, Wrench, X } from "lucide-react"

import { cn } from "../../lib/utils"
import { Combobox, type ComboboxOption } from "../ui/combobox"
import { StatusBadge } from "../ui/status-badge"
import { PortalContainerContext } from "../ui/portal-container"
import { useFloatingPanelPosition } from "../ui/use-floating-panel-position"
import { reasonLabel } from "./run-verdict-tabs"

// A single "Filters" toolbar button that opens an e-shop-style filter menu:
// marketplace category, Magento (source) category, and — depending on the active
// verdict tab — either a gaps-only toggle (All / OK) or the per-reason facets of
// the selected NOK cohort (Fixable / Excluded). The reason facets replace the old
// inline chip strip: they narrow the grid to configs carrying one verdict reason,
// scoped to the cohort. All filters apply server-side before pagination (see
// useRunFeedPreview), so they slice the whole run, not just the loaded grid.
// Options come from the run-wide facets; the "Gaps only" count is the run
// summary's fixable tally (matches the header gap badge). Hand-rolled popover
// (outside-click + Escape) to match the Combobox primitive — no extra dependency.

type RunCategoryFiltersProps = {
  facets: RunFacetsResponse | undefined
  marketplaceCategory: string | null
  onMarketplaceCategoryChange: (value: string | null) => void
  magentoCategoryLeaf: string | null
  onMagentoCategoryChange: (value: string | null) => void
  /** The gaps-only toggle is shown only outside the NOK view (the Fixable cohort
   *  already scopes NOK to gap configs). Null ⇒ hide the toggle entirely. */
  gapsOnly: boolean | null
  onGapsOnlyChange: (value: boolean) => void
  gapsCount: number
  /** Per-reason facets for the active NOK cohort, each a selectable chip that
   *  narrows the grid to configs carrying that reason. Empty ⇒ no reason section
   *  (e.g. on All / OK, or a cohort with no reasons). */
  reasonFacets: readonly RunVerdictReasonTally[]
  selectedReason: string | null
  onReasonChange: (reason: string | null) => void
}

export function RunCategoryFilters(props: RunCategoryFiltersProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const portalContainer = useContext(PortalContainerContext)
  const panelStyle = useFloatingPanelPosition(open, triggerRef, 288)

  // Close on an outside pointer or Escape, the same mechanics the Combobox uses.
  // Nested combobox panels use portals outside this menu, so treat their
  // marked surface as part of this menu too.
  useEffect(() => {
    if (!open) {
      return
    }
    function onPointerDown(event: PointerEvent) {
      const target = event.target
      const insideComboboxPanel =
        target instanceof Element && target.closest("[data-combobox-panel]") !== null
      if (
        rootRef.current &&
        !rootRef.current.contains(target as Node) &&
        !panelRef.current?.contains(target as Node) &&
        !insideComboboxPanel
      ) {
        setOpen(false)
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false)
      }
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  const activeCount =
    (props.marketplaceCategory ? 1 : 0) +
    (props.magentoCategoryLeaf ? 1 : 0) +
    (props.gapsOnly ? 1 : 0) +
    (props.selectedReason ? 1 : 0)

  return (
    <div ref={rootRef} className="relative">
      <FilterTriggerButton
        ref={triggerRef}
        activeCount={activeCount}
        open={open}
        onToggle={() => setOpen((isOpen) => !isOpen)}
      />

      {open &&
        createPortal(
          <FilterMenuPanel
            {...props}
            ref={panelRef}
            style={panelStyle}
            activeCount={activeCount}
          />,
          portalContainer ?? document.body
        )}
    </div>
  )
}

// The toolbar trigger: a tab-sized bordered button showing how many filters are
// active. Filled when active or open, like the segmented controls beside it.
function FilterTriggerButton({
  ref,
  activeCount,
  open,
  onToggle,
}: {
  ref: RefObject<HTMLButtonElement | null>
  activeCount: number
  open: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      ref={ref}
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={onToggle}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
        activeCount > 0 || open
          ? "bg-muted text-foreground"
          : "text-muted-foreground hover:text-foreground"
      )}
    >
      <ListFilter className="size-3.5 shrink-0" />

      <span>Filters</span>

      {activeCount > 0 && (
        <span className="ml-0.5 inline-flex min-w-4 items-center justify-center rounded-full bg-foreground px-1 text-xs font-semibold tabular-nums text-background">
          {activeCount}
        </span>
      )}
    </button>
  )
}

// The dropdown panel: two category comboboxes, then — per the active view —
// either the gaps-only checkbox or the cohort's reason facets, plus a "Clear
// all" reset shown only when something is active.
function FilterMenuPanel({
  ref,
  style,
  facets,
  marketplaceCategory,
  onMarketplaceCategoryChange,
  magentoCategoryLeaf,
  onMagentoCategoryChange,
  gapsOnly,
  onGapsOnlyChange,
  gapsCount,
  reasonFacets,
  selectedReason,
  onReasonChange,
  activeCount,
}: RunCategoryFiltersProps & {
  ref: RefObject<HTMLDivElement | null>
  style: CSSProperties
  activeCount: number
}) {
  const marketplaceOptions: ComboboxOption[] = (facets?.marketplaceCategories ?? []).map((c) => ({
    value: c.code,
    label: `${c.label} (${c.count})`,
  }))
  // Breadcrumb rides as `keywords` so a same-named leaf is searchable by its full
  // path even though the dropdown shows just the leaf.
  const magentoOptions: ComboboxOption[] = (facets?.magentoCategories ?? []).map((c) => ({
    value: c.leaf,
    label: `${c.leaf} (${c.count})`,
    keywords: c.breadcrumb ?? undefined,
  }))

  function clearAll() {
    onMarketplaceCategoryChange(null)
    onMagentoCategoryChange(null)
    onGapsOnlyChange(false)
    onReasonChange(null)
  }

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Product filters"
      style={style}
      className="motion-floating-surface fixed z-50 overflow-y-auto rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-floating"
    >
      <div className="mb-2.5 flex items-center justify-between">
        <span className="text-xs font-semibold text-foreground">Filters</span>

        {activeCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="size-3" />
            Clear all
          </button>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <FilterField label="Marketplace category">
          <Combobox
            options={marketplaceOptions}
            value={marketplaceCategory ?? undefined}
            onChange={(value) => onMarketplaceCategoryChange(value ?? null)}
            placeholder="All categories"
            searchPlaceholder="Filter categories…"
            clearLabel="All categories"
            className="w-full"
          />
        </FilterField>

        <FilterField label="Magento category">
          <Combobox
            options={magentoOptions}
            value={magentoCategoryLeaf ?? undefined}
            onChange={(value) => onMagentoCategoryChange(value ?? null)}
            placeholder="All categories"
            searchPlaceholder="Filter categories…"
            clearLabel="All categories"
            className="w-full"
          />
        </FilterField>

        {gapsOnly !== null && (
          <button
            type="button"
            aria-pressed={gapsOnly}
            onClick={() => onGapsOnlyChange(!gapsOnly)}
            className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-left text-xs text-foreground transition-colors hover:bg-muted/60"
          >
            <span
              className={cn(
                "flex size-4 shrink-0 items-center justify-center rounded border transition-colors",
                gapsOnly ? "border-foreground bg-foreground text-background" : "border-border"
              )}
            >
              {gapsOnly && <Check className="size-3" />}
            </span>

            <span>Gaps only</span>

            <span className="ml-auto tabular-nums text-muted-foreground">{gapsCount}</span>
          </button>
        )}

        {reasonFacets.length > 0 && (
          <FilterField label="Reason">
            <div className="flex flex-wrap gap-1.5">
              {reasonFacets.map((facet) => (
                <ReasonFacetChip
                  key={facet.reason}
                  tally={facet}
                  selected={selectedReason === facet.reason}
                  onToggle={() =>
                    onReasonChange(selectedReason === facet.reason ? null : facet.reason)
                  }
                />
              ))}
            </div>
          </FilterField>
        )}
      </div>
    </div>
  )
}

// A reason facet chip inside the Filters popover. Toggling it narrows the grid to
// configs carrying that reason; the tone + icon encode fixable (wrench/warning)
// vs won't-list (ban/destructive), mirroring the run's language.
function ReasonFacetChip({
  tally,
  selected,
  onToggle,
}: {
  tally: RunVerdictReasonTally
  selected: boolean
  onToggle: () => void
}) {
  const Icon = tally.fixable ? Wrench : Ban
  return (
    <button type="button" onClick={onToggle} aria-pressed={selected} className="appearance-none">
      <StatusBadge
        tone={tally.fixable ? "warning" : "destructive"}
        className={cn(
          "cursor-pointer ring-offset-1 ring-offset-popover transition-shadow hover:brightness-95",
          selected && (tally.fixable ? "ring-2 ring-warning/60" : "ring-2 ring-destructive/60")
        )}
      >
        <Icon className="size-3 shrink-0" />

        {reasonLabel(tally.reason)}

        <span className="opacity-70">{tally.count}</span>
      </StatusBadge>
    </button>
  )
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>

      {children}
    </div>
  )
}
